import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@baluu/ui/components/button";
import { Spinner } from "@baluu/ui/components/spinner";
import { validateEmail, validateName } from "@baluu/ui/components/checkout";
import { PersonalDetailsForm } from "@baluu/ui/components/checkout/personal-details-form";
import { MandatoryAcceptanceCheckbox } from "@baluu/ui/components/checkout/mandatory-acceptance-checkbox";
import { getSourceInfo } from "~/widgets/lib/getSourceInfo";
import { fetchSchoolCheckoutConfig } from "~/widgets/lib/school-checkout-config";
import { confirmBasketOrder, resolveOverlaySessionOutcome } from "~/widgets/components/BasketCheckoutOverlay/basketSessionOutcome";
import type { ViewContext } from "../../types";
import { BookingApiError, messageOf, sendJson } from "../../api";
import { Notice } from "../../parts";
import { formatMoney } from "../../money";
import {
  checkoutBody,
  describePaymentOptions,
  isStaleOrderError,
  itemsCharge,
  lineOf,
  type Buyer,
  type CheckoutSessionResponse,
  type PayBookingPage,
} from "./payBooking";
import { StripePaymentDialog } from "./StripePaymentDialog";
import { goToHostedCheckout } from "./stripeReturn";

// The pay step of a booking's payment link: the order's lines (fixed: the
// school built them), the deposit-or-in-full choice when the school offers
// it, the customer's details (filled in from the booking), the school's
// terms, and the card form. The same calls v1's basket checkout makes for a
// partner order.

type SchoolConfig = {
  stripePublicKey?: string;
  termsSettings?: { termsAndConditions?: { mandatoryTickBox?: boolean } };
};

type Intent = { clientSecret: string; publishableKey: string; stripeAccount?: string; amount: number; currency: string };

const START_FAILED = "We couldn’t start your payment. Please try again in a moment.";

export function PartnerCheckout({
  ctx,
  page,
  returnUrl,
  onPaid,
  onOrderMoved,
}: {
  ctx: ViewContext;
  page: PayBookingPage;
  /** This page on this host, with the link query. */
  returnUrl: string;
  onPaid: () => void;
  /** The order changed under the page: re-read it. */
  onOrderMoved: () => void;
}) {
  const [buyer, setBuyer] = useState<Buyer>(() => ({
    firstName: page.buyer?.firstName ?? "",
    lastName: page.buyer?.lastName ?? "",
    email: page.buyer?.email ?? "",
    phone: page.buyer?.phone ?? "",
  }));
  const [touched, setTouched] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [starting, setStarting] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [intent, setIntent] = useState<Intent | null>(null);

  const schoolId = page.items?.[0]?.schoolId || ctx.businessId;
  const config = useQuery({
    queryKey: ["booking", "checkout-config", ctx.api, schoolId],
    queryFn: () => fetchSchoolCheckoutConfig<SchoolConfig>(schoolId, ctx.api),
    enabled: !!schoolId,
    staleTime: 5 * 60 * 1000,
  });

  const currency = page.currency || page.items?.[0]?.currency || "GBP";
  const money = (pence: number) => formatMoney(pence, currency);
  const timezone = page.school?.timezone ?? null;
  const mandatoryTickBox = !!config.data?.termsSettings?.termsAndConditions?.mandatoryTickBox;
  const errors = {
    firstName: validateName(buyer.firstName, "First name").error,
    lastName: validateName(buyer.lastName, "Last name").error,
    email: validateEmail(buyer.email).error,
  };
  const invalid = !!(errors.firstName || errors.lastName || errors.email);
  const busy = starting || choosing || intent !== null;
  const options = page.paymentOptions ? describePaymentOptions(page.paymentOptions, money) : null;

  async function choose(option: "deposit" | "full") {
    if (busy || option === page.paymentOptions?.selected) return;
    setChoosing(true);
    setError(null);
    try {
      await sendJson(`${ctx.api}/app/pages/secure-payment/booking/${encodeURIComponent(page.basketOrderId)}/payment-option`, "POST", { option });
    } catch (e) {
      setError(messageOf(e, "We couldn’t change the payment option. Please re-open the payment link from your email."));
    }
    // Success or not, the order's own figure is the truth: re-read it.
    onOrderMoved();
    setChoosing(false);
  }

  async function pay() {
    setTouched(true);
    if (busy || invalid || (mandatoryTickBox && !acceptedTerms) || config.isLoading) return;
    setStarting(true);
    setError(null);
    try {
      const session = await sendJson<CheckoutSessionResponse>(
        `${ctx.api}/widget/app/createBasketCheckoutSession`,
        "POST",
        checkoutBody({ page, buyer, sourceInfo: { ...getSourceInfo() }, returnUrl }),
      );
      const outcome = resolveOverlaySessionOutcome(session);
      if (outcome.kind === "confirm") {
        if (outcome.completionVariant !== "paid") throw new Error("not payable");
        await confirmBasketOrder(ctx.api, session.basketOrderId, START_FAILED);
        onPaid();
        return;
      }
      if (outcome.kind === "hosted") {
        goToHostedCheckout(outcome.url);
        return;
      }
      const publishableKey = session.stripePublicKey || config.data?.stripePublicKey;
      if (!session.clientSecret || !publishableKey) throw new Error("no payment form");
      setIntent({
        clientSecret: session.clientSecret,
        publishableKey,
        stripeAccount: session.paymentMethodType === "direct" ? session.schoolAccountID : undefined,
        amount: session.amount ?? itemsCharge(page.items),
        currency: session.currency || currency,
      });
    } catch (e) {
      if (e instanceof BookingApiError && isStaleOrderError(e.status, e.message)) {
        setError(e.message);
        onOrderMoved();
      } else {
        setError(messageOf(e, START_FAILED));
      }
    } finally {
      setStarting(false);
    }
  }

  async function paid(paymentIntentId?: string) {
    setIntent(null);
    if (paymentIntentId) {
      try {
        await sendJson(`${ctx.api}/widget/app/processBasketPayment`, "POST", {
          basketOrderId: page.basketOrderId,
          paymentIntentId,
        });
      } catch {
        // The payment went through: the webhook records it if this didn't.
      }
    }
    onPaid();
  }

  return (
    <div className="space-y-6">
      <section aria-labelledby="pay-booking-lines" className="space-y-3 rounded-lg border bg-card p-5">
        <h2 id="pay-booking-lines" className="font-semibold">
          Your booking
        </h2>
        <ul className="space-y-3">
          {(page.items ?? []).map((item) => {
            const line = lineOf(item, timezone);
            return (
              <li key={item.id} className="flex justify-between gap-4 text-sm">
                <span>
                  <span className="block font-medium">{line.title}</span>
                  {line.when && <span className="block text-muted-foreground">{line.when}</span>}
                  {line.tickets && <span className="block text-muted-foreground">{line.tickets}</span>}
                </span>
                <span className="whitespace-nowrap">{money(line.amount)}</span>
              </li>
            );
          })}
        </ul>
        <div className="flex justify-between border-t pt-3 text-sm font-semibold">
          <span>To pay now</span>
          <span>{money(itemsCharge(page.items))}</span>
        </div>
      </section>

      {options && (
        <section aria-labelledby="pay-booking-option" className="space-y-3 rounded-lg border bg-card p-5">
          <h2 id="pay-booking-option" className="flex items-center gap-2 font-semibold">
            How much would you like to pay today?
            {choosing && <Spinner className="size-4" />}
          </h2>
          <div role="radiogroup" aria-label="Payment amount" className="space-y-2">
            {options.map((o) => {
              const checked = o.value === page.paymentOptions?.selected;
              return (
                <label
                  key={o.value}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${checked ? "border-primary bg-primary/5" : ""} ${busy ? "cursor-wait opacity-60" : ""}`}
                >
                  <input
                    type="radio"
                    name="partner-payment-option"
                    value={o.value}
                    checked={checked}
                    disabled={busy}
                    onChange={() => void choose(o.value)}
                    className="mt-1"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium">{o.title}</span>
                    <span className="block text-xs text-muted-foreground">{o.detail}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </section>
      )}

      <section aria-labelledby="pay-booking-details" className="space-y-4 rounded-lg border bg-card p-5">
        <h2 id="pay-booking-details" className="font-semibold">
          Your details
        </h2>
        <PersonalDetailsForm
          values={{ ...buyer, marketingOptIn: false }}
          errors={touched ? errors : {}}
          touched={touched ? { firstName: true, lastName: true, email: true } : {}}
          onChange={(field, value) => {
            if (field === "firstName" || field === "lastName" || field === "email") setBuyer((b) => ({ ...b, [field]: String(value) }));
          }}
          disabled={busy}
          showPhone={false}
          showMarketingOptIn={false}
        />
        {mandatoryTickBox && (
          <MandatoryAcceptanceCheckbox
            checked={acceptedTerms}
            onCheckedChange={setAcceptedTerms}
            onOpenTerms={() => window.open(ctx.links.terms(), "_blank", "noopener")}
            touched={touched}
            disabled={busy}
          />
        )}
        {!mandatoryTickBox && (
          <p className="text-xs text-muted-foreground">
            By paying you agree to {page.school?.name || "the school"}’s{" "}
            <a className="underline" href={ctx.links.terms()} target="_blank" rel="noreferrer">
              terms and conditions
            </a>
            .
          </p>
        )}
      </section>

      {error && <Notice tone="error">{error}</Notice>}
      {config.isError && (
        <Notice tone="error">
          We couldn’t load the school’s payment settings.{" "}
          <button type="button" className="underline" onClick={() => config.refetch()}>
            Try again
          </button>
        </Notice>
      )}

      <Button className="w-full sm:w-auto" onClick={() => void pay()} disabled={busy || config.isLoading || config.isError}>
        {starting && <Spinner className="mr-2 size-4" />}
        Pay {money(itemsCharge(page.items))}
      </Button>

      {intent && (
        <StripePaymentDialog
          publishableKey={intent.publishableKey}
          clientSecret={intent.clientSecret}
          stripeAccount={intent.stripeAccount}
          amountLabel={formatMoney(intent.amount, intent.currency)}
          returnUrl={returnUrl}
          primaryColor={ctx.primaryColor}
          onPaid={(id) => void paid(id)}
          onClose={() => setIntent(null)}
        />
      )}
    </div>
  );
}
